/**
 * Simple API client for making fetch requests to internal API endpoints
 * Automatically sets common headers and handles errors
 */
import CryptoJS from 'crypto-js'

interface IApiOptions {
  body?: Record<string, unknown>
  method?: 'DELETE' | 'GET' | 'POST' | 'PUT'
}

interface IApiResponse<T> {
  data?: T
  error?: string
  success: boolean
}

/**
 * Make an API request to an internal endpoint
 * @param endpoint - API endpoint path (e.g., '/api/meta-conversion')
 * @param options - Request options
 * @returns Promise with response data
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function apiRequest<T = any>(
  endpoint: string,
  options: IApiOptions = {}
): Promise<IApiResponse<T>> {
  const { body, method = body ? 'POST' : 'GET' } = options

  try {
    const response = await fetch(endpoint, {
      body: body ? JSON.stringify(body) : undefined,
      headers: {
        'Content-Type': 'application/json',
      },
      method,
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }))
      console.error(`API error (${endpoint}):`, errorData)
      return {
        error: errorData.error || `API error: ${response.status}`,
        success: false,
      }
    }

    const data = await response.json()
    return {
      data,
      success: true,
    }
  } catch (error) {
    console.error(`API request failed (${endpoint}):`, error)
    return {
      error: error instanceof Error ? error.message : 'Unknown error',
      success: false,
    }
  }
}

/**
 * Hash user data for privacy
 * @param userData - User data to hash
 * @returns Hashed user data
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function hashUserData(userData: Record<string, any>): Record<string, string> {
  const hashedData: Record<string, string> = {}

  // Process each field that should be hashed
  Object.keys(userData).forEach((key) => {
    if (userData[key] && typeof userData[key] === 'string') {
      const value = userData[key].toLowerCase().trim()
      // Only hash non-empty values
      if (value) {
        hashedData[key] = CryptoJS.SHA256(value).toString(CryptoJS.enc.Hex)
      }
    }
  })

  return hashedData
}

/**
 * Track a conversion event via the meta-conversion API
 * @param eventName - Name of the event
 * @param userData - User data for the event (will be hashed)
 * @param eventId - Optional event ID
 * @param additionalParams - Additional conversion parameters
 */
export function trackMetaConversion(
  eventName: string,
  userData: { [key: string]: unknown; em?: string },
  eventId?: string,
  additionalParams?: {
    browser_id?: string
    click_id?: string
    external_id?: string
  }
): Promise<IApiResponse<unknown>> {
  // Hash user data before sending
  const hashedUserData = hashUserData(userData)

  return apiRequest('/api/meta-conversion', {
    body: {
      browser_id: additionalParams?.browser_id,
      // Add additional parameters if provided
      click_id: additionalParams?.click_id,
      event_id: eventId || `${eventName}_${Date.now()}`,
      event_name: eventName,
      external_id: additionalParams?.external_id,
      user_data: hashedUserData,
    },
  })
}
